import React, {useMemo} from 'react';
import {Form, Modal} from 'react-bootstrap';
import {useDropzone} from 'react-dropzone';
import {loadCustumFont} from '@pulsoid-private/widgets-lib';
import {ActionWithProgress, Secondary} from '../../buttons';

import css from './FontUploadModal.module.css';
import classNames from 'classnames';

export type FontUploadModalProps = {
  show: boolean;
  onClose: () => void;
  onSubmit?: (font: {name: string; file: File}) => void;
};

const DEFAULT_FONT_NAME = 'My Font';

const ERROR_TYPES = {
  TWO_FILES: 'TWO_FILES',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  NO_FILE: 'NO_FILE',
  INVALID_NAME_CHARACTER: 'INVALID_NAME_CHARACTER',
  INVALID_NAME_SIZE: 'INVALID_NAME_SIZE',
} as const;

const ERROR_MAPPING = {
  [ERROR_TYPES.TWO_FILES]: 'You can only upload one file at a time',
  [ERROR_TYPES.INVALID_FILE_TYPE]:
    'You can only upload .ttf, .otf, .woff, .woff2 files',
  [ERROR_TYPES.NO_FILE]: 'You must upload a file',
  [ERROR_TYPES.INVALID_NAME_CHARACTER]:
    'Font name contains invalid characters.',
  [ERROR_TYPES.INVALID_NAME_SIZE]: 'Font name is too long.',
};

const validateFontFiles = (
  file: File
): typeof ERROR_TYPES[keyof typeof ERROR_TYPES] | null => {
  if (
    ![
      'application/x-font-ttf',
      'application/x-font-otf',
      'application/x-font-woff',
      'application/x-font-woff2',
      'font/ttf',
      'font/otf',
      'font/woff',
      'font/woff2',
    ].includes(file?.type)
  ) {
    return ERROR_TYPES.INVALID_FILE_TYPE;
  }

  return null;
};

export const FontUploadModal = (props: FontUploadModalProps) => {
  const [fontName, setFontName] = React.useState('');
  const [fontFile, setFontFile] = React.useState<File | null>(null);

  const fontNameError = useMemo(() => {
    if (!fontName) {
      return null;
    }

    if (!/^[a-zA-Z0-9_-\s]+$/.test(fontName.trim())) {
      return ERROR_MAPPING[ERROR_TYPES.INVALID_NAME_CHARACTER];
    }

    if (fontName.length > 50) {
      return ERROR_MAPPING[ERROR_TYPES.INVALID_NAME_SIZE];
    }

    return null;
  }, [fontName]);

  const [fontToDisplay, setFontToDisplay] = React.useState<string | null>(null);

  const cleanStateAndClose = () => {
    setFontName('');
    setFontFile(null);
    setFontToDisplay(null);
    props.onClose();
  };

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    isDragActive,
  } = useDropzone({
    maxFiles: 1,
    multiple: false,
    validator: (file) => {
      const error = validateFontFiles(file);

      if (error) {
        return {
          code: error,
          message: ERROR_MAPPING[error],
        };
      }

      return null;
    },
    onDrop: async (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 1 || fileRejections.length > 0) {
        return;
      }

      const fontFile = acceptedFiles[0];
      if (fontFile && fontFile.type?.includes('font')) {
        const newFontName = fontFile?.name?.split('.')[0] || DEFAULT_FONT_NAME;
        await loadCustumFont({
          name: newFontName,
          file: acceptedFiles[0],
        });
        setFontToDisplay(newFontName);
        setFontName(newFontName);
        setFontFile(acceptedFiles[0]);
      }
    },
  });

  return (
    <Modal show={props.show} onHide={cleanStateAndClose}>
      <Modal.Header closeButton>
        <Modal.Title>Upload Font</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          {...getRootProps()}
          className={classNames(css.uploadDropZone, {
            [css.uploadDropZoneFocused]: isFocused,
            [css.uploadDropZoneAccept]: isDragAccept,
            [css.uploadDropZoneReject]: isDragReject,
            [css.uploadDropZoneActive]: isDragActive,
          })}
        >
          <input {...getInputProps()} />
          <p
            className={classNames(css.uploadDropZonePlaceholder, {
              [css.uploadDropZonePlaceholderEmpty]: !fontFile,
            })}
          >
            Drop font here or click to select font file (otf, ttf)
          </p>
          {fontFile ? (
            <p
              style={{
                fontFamily: fontToDisplay ? `"${fontToDisplay}"` : 'sans-serif',
              }}
            >
              HEART RATE: 1234567890
            </p>
          ) : null}
        </div>

        <Form>
          <Form.Group>
            <Form.Label htmlFor="font.name">Font Name</Form.Label>
            <Form.Control
              id="font.name"
              type="text"
              placeholder={fontFile ? 'Font Name' : 'Select a font first'}
              disabled={!fontFile}
              value={fontName}
              onChange={(e) => setFontName(e.target.value)}
              isInvalid={!!fontNameError}
            />
            <Form.Control.Feedback type="invalid">
              {fontNameError}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Secondary onClick={cleanStateAndClose}>Cancel</Secondary>

        <ActionWithProgress
          disabled={!fontName || !fontFile || !!fontNameError}
          onClick={async () => {
            try {
              await props.onSubmit?.({name: fontName, file: fontFile!});
              cleanStateAndClose();
            } catch (e) {
              // TODO: Handle error
            }
          }}
        >
          Upload
        </ActionWithProgress>
      </Modal.Footer>
    </Modal>
  );
};
